import React, { useId } from 'react'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { type ExecRole, type ProtectedRoleHolder } from 'services/lutValuesService'

interface ExecRoleSelectProps {
  label: string
  value: ExecRole | null
  options: ExecRole[]
  disabled?: boolean
  required?: boolean
  onChange: (next: ExecRole | null) => void
  protectedHolders?: ProtectedRoleHolder[]
  currentScope?: { stateId: number | null, topOrgId: number | null }
  excludePositionId?: number
}

const findHolderElsewhere = (
  roleId: number,
  protectedHolders: ProtectedRoleHolder[],
  currentScope: { stateId: number | null, topOrgId: number | null },
  excludePositionId: number
): ProtectedRoleHolder | undefined =>
  protectedHolders.find(holder => {
    if (holder.roleId !== roleId || holder.positionId === excludePositionId) return false
    if (holder.scope === 'NATIONAL') return true
    if (holder.scope === 'STATE') return holder.scopeValue === currentScope.stateId
    return holder.scopeValue === currentScope.topOrgId
  })

export const ExecRoleSelect: React.FC<ExecRoleSelectProps> = ({
  label, value, options, disabled = false, required = false, onChange,
  protectedHolders = [], currentScope = { stateId: null, topOrgId: null }, excludePositionId = 0
}) => {
  const labelId = useId()
  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value?.id ?? ''}
        onChange={e => {
          const v = e.target.value
          if (v === '') {
            onChange(null)
          } else {
            const match = options.find(r => r.id === Number(v))
            if (match != null) onChange(match)
          }
        }}
      >
        {!required && <MenuItem value=''>None</MenuItem>}
        {options.map(r => {
          const holder = findHolderElsewhere(r.id, protectedHolders, currentScope, excludePositionId)
          return (
            <MenuItem key={r.id} value={r.id} disabled={holder != null}>
              {holder != null ? `${r.descShort} — held by ${holder.holderName} (position ${holder.positionId})` : r.descShort}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}
